import { CONSTANT } from "src/app/constant";
import { DraftItem } from "src/app/models/models";
import { SESSION } from "src/session.config";

export interface UnsyncedDraftBackupPayload {
	savedAt: string;
	drafts: DraftItem[];
}

const DRAFT_STORE = CONSTANT.INDEXEDDB.DRAFT;

function openDbWithoutVersion(dbName: string): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(dbName);
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function readAllFromStore<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
	return new Promise((resolve, reject) => {
		if (!db.objectStoreNames.contains(storeName)) {
			resolve([]);
			return;
		}
		const tx = db.transaction(storeName, "readonly");
		const store = tx.objectStore(storeName);
		const req = store.getAll();
		req.onsuccess = () => resolve((req.result as T[]) ?? []);
		req.onerror = () => reject(req.error);
	});
}

/**
 * Open the existing DB (any version), copy unsynced drafts to localStorage, then close.
 * Returns the number of drafts backed up. Safe to call when a VersionError is imminent.
 */
export async function backupUnsyncedDraftsFromIndexedDb(dbName: string): Promise<number> {
	if (typeof indexedDB === "undefined" || typeof localStorage === "undefined") {
		return 0;
	}

	let db: IDBDatabase | undefined;
	try {
		db = await openDbWithoutVersion(dbName);
		const all = await readAllFromStore<DraftItem>(db, DRAFT_STORE);
		const unsynced = (all ?? []).filter((d) => d && d.synced !== true);
		if (!unsynced.length) {
			return 0;
		}

		const payload: UnsyncedDraftBackupPayload = {
			savedAt: new Date().toISOString(),
			drafts: unsynced,
		};
		localStorage.setItem(SESSION.SESSION_KEY_UNSYNCED_DRAFT_BACKUP, JSON.stringify(payload));
		console.warn(
			`[eTrainingDB] Backed up ${unsynced.length} unsynced draft(s) before IndexedDB reset. Restore key: ${SESSION.SESSION_KEY_UNSYNCED_DRAFT_BACKUP}`,
		);
		return unsynced.length;
	} catch (err) {
		console.warn("[eTrainingDB] Could not backup unsynced drafts before reset:", err);
		return 0;
	} finally {
		try {
			db?.close();
		} catch {
			/* ignore */
		}
	}
}

export function readUnsyncedDraftBackup(): UnsyncedDraftBackupPayload | null {
	if (typeof localStorage === "undefined") return null;
	const raw = localStorage.getItem(SESSION.SESSION_KEY_UNSYNCED_DRAFT_BACKUP);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as UnsyncedDraftBackupPayload;
		if (!parsed || !Array.isArray(parsed.drafts)) return null;
		return parsed;
	} catch {
		return null;
	}
}

export function clearUnsyncedDraftBackup(): void {
	if (typeof localStorage === "undefined") return;
	localStorage.removeItem(SESSION.SESSION_KEY_UNSYNCED_DRAFT_BACKUP);
}

/**
 * Write backed-up drafts into an existing IDB draft store via put().
 * Does not create/open a new schema — caller must ensure eTrainingDB stores exist first
 * (e.g. via ETrainingIdbService.ensureInitialized). Leaves backup in place if restore fails.
 */
export async function restoreUnsyncedDraftBackupToIndexedDb(dbName: string): Promise<number> {
	const backup = readUnsyncedDraftBackup();
	if (!backup?.drafts?.length) {
		return 0;
	}

	let db: IDBDatabase | undefined;
	try {
		db = await openDbWithoutVersion(dbName);
		if (!db.objectStoreNames.contains(DRAFT_STORE)) {
			console.warn("[eTrainingDB] Draft store missing during backup restore; leaving backup in place.");
			return 0;
		}

		await new Promise<void>((resolve, reject) => {
			const tx = db!.transaction(DRAFT_STORE, "readwrite");
			const store = tx.objectStore(DRAFT_STORE);
			for (const draft of backup.drafts) {
				if (!draft?.id) continue;
				store.put(draft);
			}
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(tx.error);
		});

		clearUnsyncedDraftBackup();
		console.warn(`[eTrainingDB] Restored ${backup.drafts.length} unsynced draft(s) after IndexedDB reset.`);
		return backup.drafts.length;
	} catch (err) {
		console.error("[eTrainingDB] Failed to restore unsynced draft backup:", err);
		return 0;
	} finally {
		try {
			db?.close();
		} catch {
			/* ignore */
		}
	}
}
